import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useDeleteWorkspaceLink,
  useSaveWorkspaceLink,
  useWorkspaceLinks,
  type WorkspaceLink,
} from '@/entities/workspace';
import { FormError } from '@/shared/forms';
import {
  Button,
  ConfirmDialog,
  ErrorState,
  IconButton,
  Icons,
  LoadingState,
  TextField,
} from '@/shared/ui';

import styles from './WorkspaceInfo.module.css';

const hostOf = (url: string) => {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
};

/** External links of the workspace: open (new tab), add, edit, remove. */
export function WorkspaceLinks({
  workspaceId,
  canManage,
}: {
  workspaceId: string;
  canManage: boolean;
}) {
  const { t } = useTranslation();
  const links = useWorkspaceLinks(workspaceId);
  const save = useSaveWorkspaceLink(workspaceId);
  const remove = useDeleteWorkspaceLink(workspaceId);
  // `null` = closed, `{}` = a new link, otherwise the edited one
  const [editing, setEditing] = useState<Partial<WorkspaceLink> | null>(null);
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [confirming, setConfirming] = useState<WorkspaceLink | null>(null);
  const list = links.data ?? [];

  const open = (link: Partial<WorkspaceLink>) => {
    setEditing(link);
    setUrl(link.url ?? '');
    setTitle(link.title ?? '');
    setError(null);
  };

  return (
    <div data-testid="workspace-links">
      {canManage && editing === null && (
        <div className={styles.bar}>
          <Button
            size="sm"
            onClick={() => {
              open({});
            }}
          >
            <Icons.plus /> {t('workspaceHeader.links.add')}
          </Button>
        </div>
      )}
      {editing !== null && (
        <form
          className={styles.linkForm}
          onSubmit={(event) => {
            event.preventDefault();
            setError(null);
            save.mutate(
              {
                ...(editing.id && { id: editing.id }),
                url: url.trim(),
                title: title.trim() || null,
              },
              {
                onSuccess: () => {
                  setEditing(null);
                },
                onError: setError,
              },
            );
          }}
        >
          <TextField
            label={t('workspaceHeader.links.url')}
            type="url"
            value={url}
            placeholder="https://"
            required
            maxLength={2048}
            autoFocus
            onChange={(event) => {
              setUrl(event.target.value);
            }}
          />
          <TextField
            label={t('workspaceHeader.links.title')}
            value={title}
            maxLength={200}
            onChange={(event) => {
              setTitle(event.target.value);
            }}
          />
          <div className={styles.row}>
            <Button type="submit" variant="primary" size="sm" disabled={save.isPending}>
              {t('common.save')}
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setEditing(null);
              }}
            >
              {t('common.cancel')}
            </Button>
          </div>
          <FormError error={error} />
        </form>
      )}
      {links.isPending && <LoadingState />}
      {links.isError && <ErrorState error={links.error} onRetry={() => void links.refetch()} />}
      {links.isSuccess && list.length === 0 && (
        <p className={styles.empty}>{t('workspaceHeader.links.empty')}</p>
      )}
      {list.length > 0 && (
        <ul className={styles.list}>
          {list.map((link) => (
            <li key={link.id} className={styles.item} data-testid="workspace-link">
              <span className={styles.icon} aria-hidden>
                <Icons.link />
              </span>
              <div className={styles.meta}>
                <a
                  className={styles.name}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={link.url}
                >
                  {link.title ?? hostOf(link.url)}
                </a>
                <span className={styles.sub}>{link.title ? hostOf(link.url) : link.url}</span>
              </div>
              {canManage && (
                <div className={styles.itemActions}>
                  <IconButton
                    label={`${t('workspaceHeader.links.edit')}: ${link.title ?? hostOf(link.url)}`}
                    icon={<Icons.edit />}
                    onClick={() => {
                      open(link);
                    }}
                  />
                  <IconButton
                    label={`${t('workspaceHeader.links.delete')}: ${link.title ?? hostOf(link.url)}`}
                    icon={<Icons.trash />}
                    onClick={() => {
                      setConfirming(link);
                    }}
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {confirming && (
        <ConfirmDialog
          title={t('workspaceHeader.links.deleteTitle')}
          message={t('workspaceHeader.links.deleteMessage', {
            name: confirming.title ?? hostOf(confirming.url),
          })}
          confirmLabel={t('workspaceHeader.links.delete')}
          pending={remove.isPending}
          onCancel={() => {
            setConfirming(null);
          }}
          onConfirm={() => {
            remove.mutate(confirming.id, {
              onSettled: () => {
                setConfirming(null);
              },
            });
          }}
        />
      )}
    </div>
  );
}
